import * as React from "react";
import { metaLazy } from "@/lib/metaLazy";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/lib/toast";
import type { ShopifyProduct } from "@/lib/shopify";

export interface WishlistItem {
  id: string;
  product_id: string;
  product_handle: string;
  product_title: string | null;
  product_image: string | null;
  product_price: number | null;
  product_currency: string | null;
  created_at: string;
}

// نوع داخليّ خفيف للتخزين المحلّيّ — نفس الحقول لكن id هو product_id
type LocalWishItem = Omit<WishlistItem, "id"> & { id: string };

const LOCAL_KEY = "lecmo:wishlist:v1";
const SIGNUP_HINT_KEY = "lecmo:wishlist:signup-hint-shown";
const SIGNUP_HINT_THRESHOLD = 3;

function readLocal(): LocalWishItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(LOCAL_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as LocalWishItem[]) : [];
  } catch {
    return [];
  }
}

function writeLocal(items: LocalWishItem[]): boolean {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.setItem(LOCAL_KEY, JSON.stringify(items));
  } catch {
    // تجاهل: قد يكون التخزين ممتلئاً أو محظوراً
    return false;
  }
  try {
    // إخطار باقي المكوّنات في نفس التبويب
    window.dispatchEvent(new Event("lecmo:wishlist-changed"));
  } catch {
    // ignore
  }
  return true;
}

/** Postgres unique violation: the product is already in this user's wishlist. */
function isDuplicateInsert(error: { code?: string } | null | undefined): boolean {
  return error?.code === "23505";
}

/**
 * Advertising/GA4 add_to_wishlist — only after a genuinely new, persisted
 * add. Fire-and-forget: analytics never blocks or fails the shopping action.
 */
function emitWishlistAdd(product: ShopifyProduct, tiktokVariantIds: readonly string[]): void {
  const price = product.node.priceRange?.minVariantPrice;
  const wlPayload = {
    id: product.node.id,
    title: product.node.title,
    price: price?.amount,
    currency: price?.currencyCode,
  };
  const quiet = (p: Promise<unknown>) => void p.catch(() => {});
  quiet(import("@/lib/ga4").then(({ trackAddToWishlist }) =>
    trackAddToWishlist(wlPayload),
  ));
  quiet(metaLazy(({ trackAddToWishlist }) =>
    trackAddToWishlist(wlPayload),
  ));
  quiet(import("@/lib/tiktokPixel").then(({ trackAddToWishlist }) =>
    trackAddToWishlist({
      productId: product.node.id,
      variantIds: tiktokVariantIds,
      title: wlPayload.title,
      price: wlPayload.price,
      currency: wlPayload.currency,
    }),
  ));
  quiet(import("@/lib/snapPixel").then(({ trackAddToWishlist }) =>
    trackAddToWishlist((product.node.variants?.edges ?? []).map((e) => e.node)),
  ));
  quiet(import("@/lib/googleAdsTag").then(({ trackAddToWishlist }) =>
    trackAddToWishlist(wlPayload),
  ));
  quiet(import("@/lib/openaiAdsPixel").then(({ trackAddToWishlist }) =>
    trackAddToWishlist(wlPayload),
  ));
  quiet(import("@/lib/funnelClient").then(({ trackFunnelEvent }) =>
    trackFunnelEvent("wishlist_to_purchase", "add_to_wishlist", { productId: product.node.id }),
  ));
}

function clearLocal() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(LOCAL_KEY);
    window.dispatchEvent(new Event("lecmo:wishlist-changed"));
  } catch {
    // ignore
  }
}

function productToItem(product: ShopifyProduct): LocalWishItem {
  const node = product.node;
  const variant = node.variants?.edges?.[0]?.node;
  const img = node.images?.edges?.[0]?.node;
  return {
    id: node.id,
    product_id: node.id,
    product_handle: node.handle,
    product_title: node.title,
    product_image: img?.url ?? null,
    product_price: variant ? Number(variant.price.amount) : null,
    product_currency: variant?.price.currencyCode ?? null,
    created_at: new Date().toISOString(),
  };
}

export function useWishlist() {
  const { user } = useAuth();
  const qc = useQueryClient();

  // ─── الحالة المحلّيّة (للزوّار) — تتزامن مع localStorage ───
  // ⚠️ SSR-safe: نبدأ دائماً بمصفوفة فارغة لتطابق render السيرفر مع أوّل render العميل،
  // ثم نحمّل من localStorage داخل useEffect لتفادي hydration mismatch.
  const [localItems, setLocalItems] = React.useState<LocalWishItem[]>([]);

  React.useEffect(() => {
    setLocalItems(readLocal());
    const sync = () => setLocalItems(readLocal());
    window.addEventListener("storage", sync);
    window.addEventListener("lecmo:wishlist-changed", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("lecmo:wishlist-changed", sync);
    };
  }, []);

  // ─── البيانات السحابيّة (للمسجّلين) ───
  const query = useQuery({
    queryKey: ["wishlist", user?.id],
    enabled: !!user,
    queryFn: async (): Promise<WishlistItem[]> => {
      const { data, error } = await supabase
        .from("wishlists")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as WishlistItem[];
    },
  });

  // ─── الدمج عند تسجيل الدخول: ادفع المحلّيّ إلى السحابة ثمّ نظّف ───
  const mergedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!user) return;
    if (mergedRef.current === user.id) return;
    mergedRef.current = user.id;

    const local = readLocal();
    if (local.length === 0) return;

    (async () => {
      try {
        const rows = local.map((it) => ({
          user_id: user.id,
          product_id: it.product_id,
          product_handle: it.product_handle,
          product_title: it.product_title,
          product_image: it.product_image,
          product_price: it.product_price,
          product_currency: it.product_currency,
        }));
        // Upsert على (user_id, product_id) لتفادي التكرار
        const { error } = await supabase
          .from("wishlists")
          .upsert(rows, { onConflict: "user_id,product_id", ignoreDuplicates: true });
        if (error) throw error;
        clearLocal();
        qc.invalidateQueries({ queryKey: ["wishlist", user.id] });
      } catch (e) {
        // فشل الدمج — نُبقي المحلّيّ كما هو
        console.warn("[wishlist] merge failed", e);
      }
    })();
  }, [user, qc]);

  // ─── المعرّفات الموحّدة (سحابة أو محلّيّ) ───
  const ids = React.useMemo(() => {
    if (user) return new Set((query.data ?? []).map((w) => w.product_id));
    return new Set(localItems.map((w) => w.product_id));
  }, [user, query.data, localItems]);

  // ─── إضافة سحابيّة ───
  const addMutation = useMutation({
    /** Resolves true only for a genuinely new persisted row. */
    mutationFn: async (product: ShopifyProduct): Promise<boolean> => {
      if (!user) throw new Error("not authenticated");
      const it = productToItem(product);
      const { error } = await supabase.from("wishlists").insert({
        user_id: user.id,
        product_id: it.product_id,
        product_handle: it.product_handle,
        product_title: it.product_title,
        product_image: it.product_image,
        product_price: it.product_price,
        product_currency: it.product_currency,
      });
      if (isDuplicateInsert(error)) return false;
      if (error) throw error;
      void import("@/lib/activityTracker").then((m) =>
        m.trackWishlist("add", it.product_handle, it.product_title ?? undefined),
      );
      return true;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["wishlist", user?.id] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const removeMutation = useMutation({
    mutationFn: async (productId: string) => {
      if (!user) throw new Error("not authenticated");
      const { error } = await supabase
        .from("wishlists")
        .delete()
        .eq("product_id", productId);
      if (error) throw error;
      void import("@/lib/activityTracker").then((m) =>
        m.trackWishlist("remove", undefined, undefined),
      );
    },

    onSuccess: () => qc.invalidateQueries({ queryKey: ["wishlist", user?.id] }),
    onError: (e: Error) => toast.error(e.message),
  });

  // ─── واجهة موحّدة: تعمل للزائر والمسجّل ───
  const add = React.useCallback(
    async (product: ShopifyProduct, tiktokContext?: { variantIds: readonly string[] }) => {
      // PDP keeps only the selected variant in the shared wishlist payload.
      // Snapshot its full product variant list separately for TikTok; leave
      // persisted wishlist details and every other platform's input unchanged.
      const tiktokVariantIds = [...(tiktokContext?.variantIds ?? (product.node.variants?.edges ?? []).map((e) => e.node.id))];
      if (user) {
        // Throws on failed persistence (no event). Duplicate → false (no event).
        const isNew = await addMutation.mutateAsync(product);
        if (isNew) emitWishlistAdd(product, tiktokVariantIds);
        return;
      }
      // زائر — احفظ محلّيّاً (reads/writes are synchronous: overlapping calls
      // see each other's write, so only the first add is "new").
      const it = productToItem(product);
      const current = readLocal();
      if (current.some((x) => x.product_id === it.product_id)) return;
      const next = [it, ...current];
      if (!writeLocal(next)) return;
      setLocalItems(next);
      emitWishlistAdd(product, tiktokVariantIds);
    },
    [user, addMutation],
  );

  const remove = React.useCallback(
    async (productId: string) => {
      if (user) {
        await removeMutation.mutateAsync(productId);
        return;
      }
      const next = readLocal().filter((x) => x.product_id !== productId);
      writeLocal(next);
      setLocalItems(next);
    },
    [user, removeMutation],
  );

  // العناصر الموحّدة (للعرض في صفحة المفضّلة وللعدّاد)
  const items: WishlistItem[] = user
    ? (query.data ?? [])
    : localItems.map((it) => ({ ...it, id: it.product_id }));

  return {
    items,
    isLoading: user ? query.isLoading : false,
    has: (productId: string) => ids.has(productId),
    add,
    remove,
    isPending: addMutation.isPending || removeMutation.isPending,
    isGuest: !user,
    guestCount: localItems.length,
    /** هل تجاوز الزائر العتبة ولم نُظهر له اقتراح التسجيل بعد؟ */
    shouldHintSignup: () => {
      if (user) return false;
      if (typeof window === "undefined") return false;
      if (localItems.length < SIGNUP_HINT_THRESHOLD) return false;
      try {
        return window.localStorage.getItem(SIGNUP_HINT_KEY) !== "1";
      } catch {
        return false;
      }
    },
    markSignupHintShown: () => {
      if (typeof window === "undefined") return;
      try {
        window.localStorage.setItem(SIGNUP_HINT_KEY, "1");
      } catch {
        // ignore
      }
    },
  };
}
